import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import {
  onboardingCompanySchema,
  type OnboardingCompanyForm,
  type CurrencyCode,
} from '@/lib/schemas';

/**
 * CompanyDetailsForm — единая форма реквизитов компании (AK-350).
 *
 * Извлечена из pages/onboarding/company.tsx, чтобы одна и та же форма
 * использовалась:
 *  - при онбординге (mode="create" → POST /companies → редирект на /dashboard);
 *  - в разделе «Компания» → вкладка «Профиль» (mode="edit" → PATCH /companies/{id}).
 *
 * Поля: name (req), short_name, legal_entity_name, inn, ogrn, kpp,
 *       legal_address, actual_address, director_name/phone/email,
 *       default_currency_code, brand_color. Валидация — zod (lib/schemas).
 *
 * Родитель отвечает за сам сетевой вызов (onSubmit) — форма не знает про
 * create vs edit, только отдаёт валидные данные и рендерит нужный CTA.
 */

const CURRENCIES = [
  { value: 'RUB', label: 'Рубль (RUB) ₽' },
  { value: 'USD', label: 'Доллар США (USD) $' },
  { value: 'EUR', label: 'Евро (EUR) €' },
  { value: 'KZT', label: 'Тенге (KZT) ₸' },
  { value: 'BYN', label: 'Белорусский рубль (BYN) Br' },
  { value: 'UZS', label: 'Сум (UZS)' },
] satisfies { value: CurrencyCode; label: string }[];

export interface CompanyDetailsFormProps {
  mode: 'create' | 'edit';
  /** Начальные значения (при edit — из загруженной компании). */
  defaultValues?: Partial<OnboardingCompanyForm>;
  /**
   * Реальный сетевой вызов. Возвращает промис; форма перехватывает ApiError
   * и показывает inline-ошибку. Успех → показываем success-alert.
   */
  onSubmit: (data: OnboardingCompanyForm) => Promise<void>;
  /** Read-only режим (нет прав редактировать) — все поля disabled. */
  disabled?: boolean;
  /** Опциональный контент справа от «Сохранить» (например «Отмена»). */
  secondaryAction?: React.ReactNode;
}

const EMPTY: OnboardingCompanyForm = {
  name: '',
  short_name: '',
  legal_entity_name: '',
  inn: '',
  ogrn: '',
  kpp: '',
  legal_address: '',
  actual_address: '',
  director_name: '',
  director_phone: '',
  director_email: '',
  default_currency_code: 'RUB',
  brand_color: '#16a34a',
};

export function CompanyDetailsForm({
  mode,
  defaultValues,
  onSubmit,
  disabled = false,
  secondaryAction,
}: CompanyDetailsFormProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isValid },
    watch,
    setValue,
  } = useForm<OnboardingCompanyForm>({
    resolver: zodResolver(onboardingCompanySchema),
    // AK-465 (добивка к PR #534): НЕ возвращать 'onBlur'. В mode:'onBlur' RHF
    // отбрасывает change-события — `isValid` не пересчитывается до blur'а, а
    // обе кнопки submit ниже = `disabled={isSubmitting || !isValid}`.
    // В mode="create" (онбординг) defaultValues пустые → isValid=false на
    // маунте → пользователь набирает «Название» и жмёт Enter/кнопку, не уводя
    // фокус → «Создать компанию» вечно disabled, а implicit submission по Enter
    // не срабатывает (браузер не диспатчит click на disabled default button).
    // 'onChange' валидирует на каждом keystroke → isValid поднимается сразу.
    mode: 'onChange',
    defaultValues: { ...EMPTY, ...defaultValues },
  });

  const brandColor = watch('brand_color');

  const submit = async (data: OnboardingCompanyForm) => {
    setServerError(null);
    setSuccess(false);
    try {
      await onSubmit(data);
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) setServerError(err.detail);
      else setServerError('Не удалось сохранить');
    }
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-5">
      {serverError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{serverError}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            {mode === 'create' ? 'Компания создана' : 'Реквизиты сохранены'}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="oc-name">Название (публичное) *</Label>
        <Input
          id="oc-name"
          placeholder="Название компании"
          aria-invalid={!!errors.name}
          className="h-10"
          disabled={disabled}
          {...register('name')}
        />
        {errors.name && (
          <p className="text-destructive text-xs mt-1">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="oc-short">Короткое название</Label>
        <Input
          id="oc-short"
          placeholder="Краткое название"
          className="h-10"
          disabled={disabled}
          {...register('short_name')}
        />
      </div>

      <Separator />
      <div className="text-sm font-medium text-muted-foreground">
        Юр. реквизиты
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="oc-legal">Полное наименование</Label>
        <Input
          id="oc-legal"
          placeholder="ООО «Полное наименование»"
          className="h-10"
          disabled={disabled}
          {...register('legal_entity_name')}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-4 space-y-1.5">
          <Label htmlFor="oc-inn">ИНН</Label>
          <Input
            id="oc-inn"
            placeholder="7701234567"
            aria-invalid={!!errors.inn}
            className="h-10"
            disabled={disabled}
            {...register('inn')}
          />
          {errors.inn && (
            <p className="text-destructive text-xs mt-1">{errors.inn.message}</p>
          )}
        </div>
        <div className="md:col-span-5 space-y-1.5">
          <Label htmlFor="oc-ogrn">ОГРН</Label>
          <Input
            id="oc-ogrn"
            placeholder="1027700000000"
            aria-invalid={!!errors.ogrn}
            className="h-10"
            disabled={disabled}
            {...register('ogrn')}
          />
          {errors.ogrn && (
            <p className="text-destructive text-xs mt-1">{errors.ogrn.message}</p>
          )}
        </div>
        <div className="md:col-span-3 space-y-1.5">
          <Label htmlFor="oc-kpp">КПП</Label>
          <Input
            id="oc-kpp"
            placeholder="770101001"
            aria-invalid={!!errors.kpp}
            className="h-10"
            disabled={disabled}
            {...register('kpp')}
          />
          {errors.kpp && (
            <p className="text-destructive text-xs mt-1">{errors.kpp.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="oc-legal-addr">Юр. адрес</Label>
        <Input
          id="oc-legal-addr"
          placeholder="г. Москва, ул. …"
          className="h-10"
          disabled={disabled}
          {...register('legal_address')}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="oc-actual-addr">Фактический адрес</Label>
        <Input
          id="oc-actual-addr"
          placeholder="Совпадает с юр.адресом / другой"
          className="h-10"
          disabled={disabled}
          {...register('actual_address')}
        />
      </div>

      <Separator />
      <div className="text-sm font-medium text-muted-foreground">
        Руководство
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="oc-director">ФИО директора</Label>
        <Input
          id="oc-director"
          placeholder="Иванов Иван Иванович"
          className="h-10"
          disabled={disabled}
          {...register('director_name')}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="oc-dir-phone">Телефон</Label>
          <Input
            id="oc-dir-phone"
            placeholder="+7 900 000 00 00"
            aria-invalid={!!errors.director_phone}
            className="h-10"
            disabled={disabled}
            {...register('director_phone')}
          />
          {errors.director_phone && (
            <p className="text-destructive text-xs mt-1">
              {errors.director_phone.message}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="oc-dir-email">Email</Label>
          <Input
            id="oc-dir-email"
            type="email"
            placeholder="director@company.ru"
            aria-invalid={!!errors.director_email}
            className="h-10"
            disabled={disabled}
            {...register('director_email')}
          />
          {errors.director_email && (
            <p className="text-destructive text-xs mt-1">
              {errors.director_email.message}
            </p>
          )}
        </div>
      </div>

      <Separator />
      <div className="text-sm font-medium text-muted-foreground">Параметры</div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="oc-currency">Валюта по умолчанию</Label>
          <Controller
            name="default_currency_code"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={disabled}
              >
                <SelectTrigger id="oc-currency" className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="oc-color">Цвет бренда</Label>
          <div className="flex items-center gap-2">
            <input
              id="oc-color"
              type="color"
              className="h-10 w-14 rounded border border-input bg-background cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              value={brandColor}
              disabled={disabled}
              onChange={(e) =>
                setValue('brand_color', e.target.value, {
                  shouldValidate: true,
                })
              }
            />
            <Input
              className="h-10 font-mono"
              aria-invalid={!!errors.brand_color}
              disabled={disabled}
              {...register('brand_color')}
            />
          </div>
          {errors.brand_color && (
            <p className="text-destructive text-xs mt-1">
              {errors.brand_color.message}
            </p>
          )}
        </div>
      </div>

      {!disabled && (
        <div className="pt-2">
          {mode === 'create' ? (
            <>
              <Button
                type="submit"
                disabled={isSubmitting || !isValid}
                size="lg"
                className="w-full"
              >
                {isSubmitting ? 'Сохраняем…' : 'Создать компанию →'}
              </Button>
              <p className="mt-3 text-center text-xs text-muted-foreground">
                Можно заполнить минимум сейчас, остальное — потом в настройках
              </p>
            </>
          ) : (
            <div className="flex items-center justify-end gap-2">
              {secondaryAction}
              <Button type="submit" disabled={isSubmitting || !isValid}>
                {isSubmitting ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </div>
          )}
        </div>
      )}
    </form>
  );
}

export default CompanyDetailsForm;
